// =============================================================================
// VehiclesScreen — Commercial > Vehicles (mig 397, 28 Sep 2026)
// =============================================================================
// James, 28 Sep 2026: "the same style board but for vehicles — vehicle cost if
// outright, or loan repayments if recurring, interest, rego, servicing,
// fitout, insurance … I really just want a clean way to see the operational
// costs but not with slop of recurring."
//
// This is piece A of BK-313: the bills. Same shape as Premises — the Xero
// account on the bill is the breakdown, the account list lives in Finance
// settings (vehicle_xero_accounts), v_vehicle_spend does the grouping — plus
// the thing Premises never needed: WHICH vehicle (ap_invoices.vehicle_id,
// picked on the coding card). Capital (730 / 731) is shown, never in the
// running-cost totals, the way the bond is on Premises.
//
// Deliberately NOT here (yet): the loan instalments, interest and insurance
// premiums — BK-313 pieces 2 and 3 add them to the per-vehicle rows once the
// Loan record exists. No standing-schedule forecast either: the run rate is
// the last 12 months of real bills, nothing invented.
// =============================================================================
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  ResponsiveContainer, Tooltip as RTooltip, Legend,
} from "recharts";
import { Truck, ArrowLeft, CalendarDays, Receipt, Wallet, Info, Landmark, Car } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, KPI, Pill, Button, Spinner, EmptyState } from "@/components/ui";
import { fmtMoney, fmtDate } from "@/lib/dates";
import FuelCardPanel from "./FuelCardPanel";   // mig 405 — litres per vehicle from the fuel card report

const COLOURS: Record<string, string> = {
  "6200":  "#F97316",   // fuel
  "450":   "#10B981",   // rego & licences
  "449":   "#EAB308",   // motor vehicle expenses
  "451":   "#3B82F6",   // tolls & parking
  "453":   "#A855F7",   // servicing & repairs
  "730":   "#6366F1",   // at cost
  "731":   "#14B8A6",   // fit-out
  uncoded: "#6B7280",
};
const FALLBACK_COLOURS = ["#EC4899", "#84CC16", "#EF4444", "#0EA5E9", "#F59E0B"];
const FLEET = "__fleet__";
// Column headers for the by-vehicle table — Xero's names ("Motor Vehicle –
// Registration & Licences") are too long for a column. James, 28 Sep: "could
// be a bit cleaner with the scroll over and nested values."
const SHORT: Record<string, string> = { "6200": "Fuel", "450": "Rego", "449": "MV expenses", "451": "Tolls", "453": "Servicing", "730": "At cost", "731": "Fit-out", uncoded: "Uncoded" };

function dayKey(d: Date) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; }
function monthKey(d: Date) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; }
function fyStart(now = new Date()) { const y = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1; return new Date(y, 6, 1); }
function fyLabel(now = new Date()) { const s = fyStart(now).getFullYear(); return `FY${String(s).slice(2)}/${String(s + 1).slice(2)}`; }
function monthLabel(key: string) { const [y, m] = key.split("-"); return `${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][Number(m) - 1]} ${y.slice(2)}`; }
function monthsIntoFy(now = new Date()) { const s = fyStart(now); return (now.getFullYear() - s.getFullYear()) * 12 + (now.getMonth() - s.getMonth()) + 1; }

interface CostLine { xero_account_code: string; xero_account_name: string; account_type: string | null; in_chart: boolean; in_map: boolean; is_running_cost: boolean; sort_order: number }
const UNCODED: CostLine = { xero_account_code: "uncoded", xero_account_name: "No Xero account on the bill", account_type: null, in_chart: false, in_map: false, is_running_cost: true, sort_order: 9999 };

export default function VehiclesScreen() {
  const user = useRequireAuth();
  const canSee = hasRole(user, "manager");   // James, 28 Sep: vehicles and premises manager+; loans and insurance director-only
  const qc = useQueryClient();
  // James, 29 Sep: "how can I change bills already paid?" — pick the vehicle
  // right here on the bill row; it saves straight to the bill, paid or not.
  const [savingBill, setSavingBill] = useState<string | null>(null);
  async function setBillVehicle(billId: string, vehicleId: string) {
    setSavingBill(billId);
    const { error } = await (supabase as any).from("ap_invoices").update({ vehicle_id: vehicleId || null }).eq("ap_invoice_id", billId);
    setSavingBill(null);
    if (error) { window.alert(`Could not set the vehicle: ${error.message}`); return; }
    qc.invalidateQueries({ queryKey: ["vehicle_spend"] });
  }
  const [lineFilter, setLineFilter] = useState<string>("all");
  const [vehicleFilter, setVehicleFilter] = useState<string>("all");
  // James, 29 Sep: "the one vehicle screen is now getting a bit too busy — clean
  // it up with the nice widgets and graphs with some clean nav to the fuel
  // transactions and some of the other details at the bottom." So: the KPIs
  // and the charts stay on top for everyone; the tables live behind tabs.
  type Tab = "overview" | "vehicles" | "fuel" | "bills";
  const [tab, setTab] = useState<Tab>("overview");
  const pickVehicle = (key: string) => { setVehicleFilter(v => (v === key ? "all" : key)); setTab("bills"); };

  const today = new Date();
  const fyFrom = dayKey(fyStart(today));
  const thisMth = monthKey(today);
  const from24 = dayKey(new Date(today.getFullYear(), today.getMonth() - 23, 1));
  const from12 = monthKey(new Date(today.getFullYear(), today.getMonth() - 11, 1));

  const { data: lines = [] } = useQuery({
    queryKey: ["vehicle_accounts"], enabled: canSee,
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("v_vehicle_accounts").select("*").order("sort_order");
      if (error) throw error; return (data ?? []) as CostLine[];
    },
  });
  const { data: rows = [], isLoading, error } = useQuery({
    queryKey: ["vehicle_spend", from24], enabled: canSee,
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("v_vehicle_spend").select("*").gte("spend_date", from24).order("spend_date", { ascending: false }).limit(3000);
      if (error) throw error; return (data ?? []) as any[];
    },
  });
  const { data: vehicles = [] } = useQuery({
    queryKey: ["vehicles_for_board"], enabled: canSee,
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("vehicles")
        .select("vehicle_id, vehicle_code, rego, make, model, year, is_active, purchase_date, finance_provider, finance_end_date, assigned_to_employee_id, rego_expiry")
        .order("vehicle_code");
      if (error) throw error; return (data ?? []) as any[];
    },
  });

  const allLines = useMemo<CostLine[]>(() => {
    const out = [...lines];
    if (rows.some(r => r.cost_line === "uncoded") && !out.some(l => l.xero_account_code === "uncoded")) out.push(UNCODED);
    return out;
  }, [lines, rows]);
  const runningLines = useMemo(() => allLines.filter(l => l.is_running_cost), [allLines]);
  const capitalLines = useMemo(() => allLines.filter(l => !l.is_running_cost), [allLines]);
  const isRunning = useMemo(() => { const m = new Map(allLines.map(l => [l.xero_account_code, l.is_running_cost])); return (c: string) => m.get(c) ?? true; }, [allLines]);
  const colourFor = (code: string, i: number) => COLOURS[code] ?? FALLBACK_COLOURS[i % FALLBACK_COLOURS.length];
  const nameFor = useMemo(() => { const m = new Map(allLines.map(l => [l.xero_account_code, l.xero_account_name])); return (c: string) => m.get(c) ?? (c === "uncoded" ? UNCODED.xero_account_name : `Xero ${c}`); }, [allLines]);
  const vehicleLabel = (v: any) => [v.vehicle_code, v.rego, [v.make, v.model].filter(Boolean).join(" ")].filter(Boolean).join(" · ");
  // two lines in the table: code · rego, then the make/model small and clipped
  const vehicleCell = (label: string) => {
    const parts = label.split(" · ");
    const head = parts.slice(0, 2).join(" · "), rest = parts.slice(2).join(" · ");
    return <span className="block leading-tight"><span className="block font-medium">{head}</span>{rest && <span className="block text-[11px] text-text-muted truncate max-w-[260px]">{rest}</span>}</span>;
  };
  const money0 = (n: number) => "$" + Math.round(n).toLocaleString("en-AU");

  const runningRows = useMemo(() => rows.filter(r => isRunning(String(r.cost_line))), [rows, isRunning]);
  const capitalRows = useMemo(() => rows.filter(r => !isRunning(String(r.cost_line))), [rows, isRunning]);

  const totals = useMemo(() => {
    let fy = 0, month = 0, last12 = 0, unassigned = 0;
    for (const r of runningRows) {
      const v = Number(r.amount_ex_gst ?? 0);
      if (r.spend_date >= fyFrom) { fy += v; if (!r.vehicle_id) unassigned += v; }
      if (r.spend_month === thisMth) month += v;
      if (r.spend_month >= from12) last12 += v;
    }
    const capital = capitalRows.reduce((a, r) => a + Number(r.amount_ex_gst ?? 0), 0);
    return { fy, month, last12, avgMonth: last12 / 12, unassigned, capital };
  }, [runningRows, capitalRows, fyFrom, thisMth, from12]);

  const byLine = useMemo(() => {
    const m = new Map<string, number>();
    for (const l of runningLines) m.set(l.xero_account_code, 0);
    for (const r of runningRows) { if (r.spend_date < fyFrom) continue; const c = String(r.cost_line); m.set(c, (m.get(c) ?? 0) + Number(r.amount_ex_gst ?? 0)); }
    return [...m.entries()].map(([code, value]) => ({ code, name: nameFor(code), value })).sort((a, b) => b.value - a.value);
  }, [runningRows, runningLines, fyFrom, nameFor]);
  const byLineWithSpend = byLine.filter(c => c.value > 0);

  const monthly = useMemo(() => {
    const months: string[] = [];
    for (let i = 11; i >= 0; i--) months.push(monthKey(new Date(today.getFullYear(), today.getMonth() - i, 1)));
    const base = months.map(k => { const row: any = { month: k, label: monthLabel(k), total: 0 }; for (const l of runningLines) row[l.xero_account_code] = 0; return row; });
    const idx = new Map(base.map(r => [r.month, r]));
    for (const r of runningRows) { const hit = idx.get(r.spend_month); if (!hit) continue; const c = String(r.cost_line); const v = Number(r.amount_ex_gst ?? 0); hit[c] = (hit[c] ?? 0) + v; hit.total += v; }
    return base;
  }, [runningRows, runningLines, today]);

  // -- per vehicle, this FY: one row per vehicle plus the fleet bucket --------
  const perVehicle = useMemo(() => {
    const m = new Map<string, { key: string; label: string; active: boolean; byLine: Record<string, number>; fy: number; capital: number; count: number }>();
    for (const v of vehicles) m.set(v.vehicle_id, { key: v.vehicle_id, label: vehicleLabel(v), active: v.is_active !== false, byLine: {}, fy: 0, capital: 0, count: 0 });
    m.set(FLEET, { key: FLEET, label: "Fleet — not one vehicle", active: true, byLine: {}, fy: 0, capital: 0, count: 0 });
    for (const r of rows) {
      const key = r.vehicle_id ?? FLEET;
      const hit = m.get(key) ?? m.set(key, { key, label: [r.vehicle_code, r.rego, r.vehicle_name].filter(Boolean).join(" · ") || "Unknown vehicle", active: true, byLine: {}, fy: 0, capital: 0, count: 0 }).get(key)!;
      const v = Number(r.amount_ex_gst ?? 0);
      if (!isRunning(String(r.cost_line))) { hit.capital += v; continue; }
      if (r.spend_date < fyFrom) continue;
      hit.byLine[String(r.cost_line)] = (hit.byLine[String(r.cost_line)] ?? 0) + v;
      hit.fy += v; hit.count += 1;
    }
    const mo = monthsIntoFy(today);
    return [...m.values()]
      .filter(x => x.key !== FLEET || x.fy > 0 || x.capital > 0)
      .map(x => ({ ...x, perMonth: x.fy / mo }))
      .sort((a, b) => (a.key === FLEET ? 1 : b.key === FLEET ? -1 : b.fy - a.fy));
  }, [rows, vehicles, fyFrom, isRunning, today]);

  const bills = useMemo(() => rows
    .filter(r => lineFilter === "all" || String(r.cost_line) === lineFilter)
    .filter(r => vehicleFilter === "all" || (vehicleFilter === FLEET ? !r.vehicle_id : r.vehicle_id === vehicleFilter))
    .slice(0, 300), [rows, lineFilter, vehicleFilter]);
  const notInChart = allLines.filter(l => !l.in_chart && l.xero_account_code !== "uncoded");

  if (!canSee) {
    return <div className="p-6 max-w-md mx-auto"><Card><EmptyState title="Managers and directors" description="Vehicle costs are restricted to managers and above." icon={Truck} /></Card></div>;
  }

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto">
      <Link to="/commercial" className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3"><ArrowLeft size={14}/> Commercial dashboard</Link>

      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2"><Truck size={22} className="text-accent" /> Vehicles</h1>
          <div className="text-text-muted text-[13px] mt-1">What the fleet costs to run — fuel, rego, servicing, tolls — by the Xero account on each bill, and by vehicle where the bill names one. Actuals from AP, ex-GST.</div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/assets"><Button variant="secondary" size="sm" icon={Car}>Vehicle register</Button></Link>
          <Link to="/ap"><Button variant="secondary" size="sm" icon={Wallet}>AP</Button></Link>
        </div>
      </div>

      {error && <div className="mb-4 bg-danger/10 border border-danger/30 rounded-md p-3 text-[12px] text-danger">Vehicle spend failed to load: {(error as any)?.message ?? String(error)}</div>}
      {notInChart.length > 0 && (
        <div className="mb-4 bg-warn/10 border border-warn/30 rounded-md p-3 text-[12px] text-warn">
          {notInChart.map(l => l.xero_account_code).join(", ")} {notInChart.length === 1 ? "is" : "are"} not in Ops's copy of the Xero chart of accounts yet — showing our own name for it. Sync the chart from the Xero hand-off screen once the bookkeeper has added it.
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <KPI label={`${fyLabel()} to date`} value={fmtMoney(totals.fy)} sub="running costs, ex-GST, from 1 July" icon={CalendarDays} accent />
        <KPI label="This month" value={fmtMoney(totals.month)} sub={monthLabel(thisMth)} />
        <KPI label="Average month" value={fmtMoney(totals.avgMonth)} sub="last 12 months of real bills" />
        <KPI label="Not against a vehicle" value={fmtMoney(totals.unassigned)} sub={`${fyLabel()} bills with no vehicle picked`} />
      </div>

      {/* the nav — widgets on top, the tables behind one tab each */}
      <div className="mb-4 flex items-center gap-1 p-1 bg-ink-raised border border-line rounded-md w-fit flex-wrap">
        {([["overview", "Overview"], ["vehicles", "By vehicle"], ["fuel", "Fuel card"], ["bills", "Bills"]] as [Tab, string][]).map(([k, label]) => (
          <button key={k} type="button" onClick={() => setTab(k)}
            className={`px-3 py-1.5 rounded text-[12px] transition-colors ${tab === k ? "bg-accent/15 text-accent border border-accent/30" : "text-text-muted hover:text-text border border-transparent"}`}>
            {label}{k === "bills" && (lineFilter !== "all" || vehicleFilter !== "all") ? " · filtered" : ""}
          </button>
        ))}
      </div>

      {isLoading ? <div className="py-10 flex justify-center"><Spinner/></div> : (
        <>
          {/* Overview — the charts and the top five, no tables */}
          {tab === "overview" && (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
                <Card><div className="p-4">
                  <div className="text-[13px] font-medium">{fyLabel()} by Xero account</div>
                  <div className="text-[11px] text-text-muted mb-1">{fmtMoney(totals.fy)} ex-GST so far</div>
                  {byLineWithSpend.length === 0 ? <div className="py-10 text-center text-[12px] text-text-muted italic">Nothing billed to the vehicle accounts this financial year yet.</div> : (
                    <div style={{ width: "100%", height: 190 }}><ResponsiveContainer><PieChart>
                      <Pie data={byLineWithSpend} dataKey="value" nameKey="name" innerRadius={52} outerRadius={78} paddingAngle={1}>
                        {byLineWithSpend.map((c, i) => <Cell key={c.code} fill={colourFor(c.code, i)} />)}
                      </Pie>
                      <RTooltip formatter={(v: any) => fmtMoney(Number(v))} />
                    </PieChart></ResponsiveContainer></div>
                  )}
                </div></Card>
                <Card className="lg:col-span-2"><div className="p-4">
                  <div className="text-[13px] font-medium">Top vehicles — {fyLabel()}</div>
                  <div className="text-[11px] text-text-muted mb-2">Running costs per vehicle, ex-GST. Click one for its bills.</div>
                  <div className="space-y-1.5">
                    {perVehicle.filter(v => v.key !== FLEET).slice(0, 6).map((v, i) => {
                      const max = Math.max(1, ...perVehicle.filter(x => x.key !== FLEET).map(x => x.fy));
                      return (
                        <button key={v.key} type="button" onClick={() => pickVehicle(v.key)}
                          className="w-full flex items-center gap-2 text-[12px] rounded px-1 -mx-1 py-1 hover:bg-ink-hover">
                          <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: FALLBACK_COLOURS[i % FALLBACK_COLOURS.length] }} />
                          <span className="text-text w-44 truncate text-left">{v.label.split(" · ").slice(0, 2).join(" · ")}</span>
                          <span className="flex-1 h-2 rounded bg-ink-hover overflow-hidden"><span className="block h-full rounded bg-accent/70" style={{ width: `${Math.round((v.fy / max) * 100)}%` }} /></span>
                          <span className="font-mono text-text w-24 text-right">{money0(v.fy)}</span>
                          <span className="font-mono text-text-muted w-20 text-right">{money0(v.perMonth)}/mo</span>
                        </button>
                      );
                    })}
                    {perVehicle.filter(v => v.key !== FLEET).length === 0 && <div className="py-6 text-center text-[12px] text-text-muted italic">No bills against a vehicle yet this year.</div>}
                    {perVehicle.some(v => v.key === FLEET) && (
                      <div className="pt-1.5 mt-1.5 border-t border-line text-[11px] text-text-muted flex items-center justify-between">
                        <span>Fleet — bills with no vehicle picked</span>
                        <span className="font-mono">{money0(perVehicle.find(v => v.key === FLEET)!.fy)}</span>
                      </div>
                    )}
                  </div>
                </div></Card>
              </div>

              <Card className="mb-4"><div className="p-4">
                <div className="text-[13px] font-medium">Month by month</div>
                <div className="text-[11px] text-text-muted mb-2">Last 12 months, ex-GST, stacked by Xero account. Running costs only.</div>
                <div style={{ width: "100%", height: 260 }}><ResponsiveContainer>
                  <BarChart data={monthly} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff14" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} tickFormatter={(v: any) => `$${Math.round(Number(v) / 1000)}k`} />
                    <RTooltip formatter={(v: any, n: any) => [fmtMoney(Number(v)), nameFor(String(n))]} contentStyle={{ background: "#111827", border: "1px solid #374151", borderRadius: 6, fontSize: 12 }} />
                    <Legend formatter={(n: any) => nameFor(String(n))} wrapperStyle={{ fontSize: 11 }} />
                    {runningLines.map((l, i) => <Bar key={l.xero_account_code} dataKey={l.xero_account_code} stackId="vehicles" fill={colourFor(l.xero_account_code, i)} />)}
                  </BarChart>
                </ResponsiveContainer></div>
              </div></Card>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                {([["vehicles", "By vehicle", "Every vehicle, every cost line, this financial year"], ["fuel", "Fuel card", "Litres per ute from the Shell report; drop a new one"], ["bills", "Bills", `${rows.length} vehicle bills in the last 24 months`]] as [Tab, string, string][]).map(([k, t, sub]) => (
                  <button key={k} type="button" onClick={() => setTab(k)} className="text-left">
                    <Card className="hover:border-accent transition-colors"><div className="p-4">
                      <div className="text-[13px] font-medium text-text">{t} →</div>
                      <div className="text-[11px] text-text-muted mt-0.5">{sub}</div>
                    </div></Card>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* By vehicle — the full table */}
          {tab === "vehicles" && (
          <Card className="mb-4">
            <div className="p-4 border-b border-line flex items-center justify-between gap-2 flex-wrap">
              <div>
                <div className="font-semibold text-[14px]">By vehicle — {fyLabel()}</div>
                <div className="text-[11px] text-text-muted mt-0.5">Running costs this financial year per vehicle, ex-GST. "Fleet" is every vehicle bill nobody has put a vehicle on — pick the vehicle on the coding card and it moves.</div>
              </div>
              {vehicleFilter !== "all" && <Button variant="secondary" size="sm" onClick={() => setVehicleFilter("all")}>Show all</Button>}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px] min-w-[860px]">
                <thead><tr className="border-b border-line">
                  <th className="text-left px-4 py-2 text-[10px] font-medium uppercase tracking-wider2 text-text-muted sticky left-0 bg-ink-raised z-10">Vehicle</th>
                  {runningLines.map(l => <th key={l.xero_account_code} className="text-right px-3 py-2 text-[10px] font-medium uppercase tracking-wider2 text-text-muted whitespace-nowrap" title={l.xero_account_name}>{SHORT[l.xero_account_code] ?? l.xero_account_name}</th>)}
                  <th className="text-right px-4 py-2 text-[10px] font-medium uppercase tracking-wider2 text-text-muted">FY total</th>
                  <th className="text-right px-4 py-2 text-[10px] font-medium uppercase tracking-wider2 text-text-muted">Per month</th>
                  <th className="text-right px-4 py-2 text-[10px] font-medium uppercase tracking-wider2 text-text-muted">Capital</th>
                </tr></thead>
                <tbody>
                  {perVehicle.map((v, i) => (
                    <tr key={v.key} onClick={() => pickVehicle(v.key)}
                        className={`border-b border-line last:border-b-0 cursor-pointer hover:bg-ink-hover ${vehicleFilter === v.key ? "bg-ink-hover" : i % 2 ? "bg-ink/40" : ""} ${v.key === FLEET ? "text-text-muted" : ""}`}>
                      <td className={`px-4 py-2 sticky left-0 z-10 ${vehicleFilter === v.key ? "bg-ink-hover" : "bg-ink-raised"}`}>{v.key === FLEET ? <span className="italic">{v.label}</span> : vehicleCell(v.label)}{!v.active && <span className="ml-1.5 text-[10px] px-1 py-0.5 rounded bg-ink-hover text-text-muted">inactive</span>}</td>
                      {runningLines.map(l => <td key={l.xero_account_code} className="px-3 py-2 font-mono text-right text-[12px]">{v.byLine[l.xero_account_code] ? money0(v.byLine[l.xero_account_code]) : <span className="text-text-dim">—</span>}</td>)}
                      <td className="px-4 py-2 font-mono text-right font-semibold">{money0(v.fy)}</td>
                      <td className="px-4 py-2 font-mono text-right text-text-muted">{money0(v.perMonth)}</td>
                      <td className="px-4 py-2 font-mono text-right text-text-muted">{v.capital ? money0(v.capital) : <span className="text-text-dim">—</span>}</td>
                    </tr>
                  ))}
                  {perVehicle.length === 0 && <tr><td colSpan={runningLines.length + 4} className="px-4 py-8 text-center text-[12px] text-text-muted italic">No vehicle bills yet this financial year.</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t border-line text-[11px] text-text-dim">
              Click a vehicle for its bills. Capital is 730 / 731 in the last 24 months — shown, not in the running totals. Litres per vehicle are on the Fuel card tab.
            </div>
          </Card>
          )}

          {/* Fuel card — mig 405 (James, 29 Sep: "who's using the most fuel") */}
          {tab === "fuel" && (
            <FuelCardPanel vehicles={vehicles}
              fuelBills={rows.filter(r => String(r.cost_line) === "6200" || r.budget_code === "OX12_FUEL")} />
          )}

          {/* Bills — where it goes, capital, and the bill list */}
          {tab === "bills" && (
          <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
            <Card className="lg:col-span-3"><div className="p-4">
              <div className="text-[13px] font-medium mb-2">Where it goes — {fyLabel()}</div>
              <div className="space-y-1.5 max-h-[220px] overflow-y-auto">
                {byLine.map((c, i) => (
                  <button key={c.code} type="button" onClick={() => setLineFilter(lineFilter === c.code ? "all" : c.code)}
                          className={`w-full flex items-center gap-2 text-[12px] rounded px-1 -mx-1 py-1 hover:bg-ink-hover ${lineFilter === c.code ? "bg-ink-hover" : ""}`}>
                    <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: colourFor(c.code, i) }} />
                    <span className="text-text flex-1 truncate text-left">{c.name}</span>
                    <span className="font-mono text-text-dim w-14 text-right">{c.code === "uncoded" ? "—" : c.code}</span>
                    <span className="font-mono text-text-muted w-10 text-right">{totals.fy > 0 ? Math.round((c.value / totals.fy) * 100) : 0}%</span>
                    <span className="font-mono text-text w-24 text-right">{fmtMoney(c.value)}</span>
                  </button>
                ))}
              </div>
              <div className="mt-3 pt-2 border-t border-line text-[11px] text-text-dim flex items-start gap-1.5">
                <Info size={12} className="mt-0.5 shrink-0"/>
                <span>Click a line to filter the bills below. The breakdown is the Xero account the bill was coded to in AP. The accounts listed come from Finance settings (vehicle_xero_accounts) — 473 is deliberately not one of them; vehicle servicing goes to 453.</span>
              </div>
            </div></Card>
          </div>

          {(capitalLines.length > 0 || capitalRows.length > 0) && (
            <Card className="mb-4">
              <div className="p-4 border-b border-line">
                <div className="font-semibold text-[14px] flex items-center gap-2"><Landmark size={15} className="text-text-muted" /> Bought outright or financed — not a running cost</div>
                <div className="text-[11px] text-text-muted mt-0.5">730 Motor Vehicles at Cost and 731 Fit-out, last 24 months. {fmtMoney(totals.capital)} ex-GST. Shown so the money is visible; left out of every total above. How each one was funded is the Loans piece.</div>
              </div>
              <div className="divide-y divide-line">
                {capitalLines.map((l, i) => {
                  const sum = capitalRows.filter(r => String(r.cost_line) === l.xero_account_code).reduce((a, r) => a + Number(r.amount_ex_gst ?? 0), 0);
                  return (
                    <button key={l.xero_account_code} type="button" onClick={() => setLineFilter(lineFilter === l.xero_account_code ? "all" : l.xero_account_code)}
                            className={`w-full px-4 py-2.5 flex items-center gap-3 text-[13px] hover:bg-ink-hover ${lineFilter === l.xero_account_code ? "bg-ink-hover" : ""}`}>
                      <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: colourFor(l.xero_account_code, i) }} />
                      <span className="flex-1 text-left">{l.xero_account_name}</span>
                      <span className="font-mono text-[11px] text-text-muted">{l.xero_account_code}</span>
                      <span className="font-mono w-28 text-right">{fmtMoney(sum)}</span>
                    </button>
                  );
                })}
              </div>
            </Card>
          )}

          <Card>
            <div className="p-4 border-b border-line flex items-center justify-between gap-2 flex-wrap">
              <div className="font-semibold text-[14px]">
                Vehicle bills
                {lineFilter !== "all" && <span className="text-text-muted font-normal"> — {nameFor(lineFilter)}</span>}
                {vehicleFilter !== "all" && <span className="text-text-muted font-normal"> — {perVehicle.find(v => v.key === vehicleFilter)?.label ?? ""}</span>}
              </div>
              {(lineFilter !== "all" || vehicleFilter !== "all") && <Button variant="secondary" size="sm" onClick={() => { setLineFilter("all"); setVehicleFilter("all"); }}>Show all</Button>}
            </div>
            {bills.length === 0 ? <EmptyState title="No vehicle bills" description="Bills on a vehicle Xero account, or with a vehicle picked on the coding card, appear here." icon={Receipt} /> : (
              <div className="overflow-x-auto"><table className="w-full text-[13px]">
                <thead><tr className="border-b border-line">
                  {["Date", "Vehicle", "Xero account", "Supplier", "Invoice #", "Description", "Ex-GST", "Inc-GST", "Status"].map(h => <th key={h} className="text-left px-4 py-2 text-[10px] font-medium uppercase tracking-wider2 text-text-muted">{h}</th>)}
                </tr></thead>
                <tbody>
                  {bills.map((b: any, i: number) => (
                    <tr key={b.ap_invoice_id} className={`border-b border-line last:border-b-0 ${i % 2 ? "bg-ink/40" : ""}`}>
                      <td className="px-4 py-2 font-mono text-[12px] text-text-muted">{fmtDate(b.spend_date)}</td>
                      <td className="px-4 py-2 text-[12px] whitespace-nowrap">
                        <select value={b.vehicle_id ?? ""} disabled={savingBill === b.ap_invoice_id}
                          onChange={e => void setBillVehicle(b.ap_invoice_id, e.target.value)}
                          title="Which vehicle is this bill for? Saves straight to the bill — paid bills too."
                          className={`bg-ink border rounded px-1.5 py-0.5 text-[12px] text-text ${b.vehicle_id ? "border-line" : "border-accent/50"}`}>
                          <option value="">fleet — not one vehicle</option>
                          {vehicles.map((v: any) => <option key={v.vehicle_id} value={v.vehicle_id}>{[v.vehicle_code, v.rego].filter(Boolean).join(" · ")}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-2 text-[12px]">{b.xero_account_name}{b.xero_account_code && <span className="font-mono text-[11px] text-text-dim ml-1.5">{b.xero_account_code}</span>}{!b.is_running_cost && <span className="ml-1.5 text-[10px] px-1 py-0.5 rounded bg-ink-hover text-text-muted">capital</span>}</td>
                      <td className="px-4 py-2 text-[12px]">{b.supplier_name ?? "—"}</td>
                      <td className="px-4 py-2 font-mono text-[11px] text-text-muted">{b.invoice_number ?? "—"}</td>
                      <td className="px-4 py-2 text-[12px] max-w-[280px] truncate">{b.description ?? "—"}</td>
                      <td className="px-4 py-2 font-mono">{fmtMoney(b.amount_ex_gst)}</td>
                      <td className="px-4 py-2 font-mono text-text-muted">{fmtMoney(b.amount_inc_gst)}</td>
                      <td className="px-4 py-2"><Pill label={String(b.status).replace(/_/g, " ")} tone={b.status === "paid" ? "approved" : b.status === "disputed" ? "rejected" : "pending"} small /></td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </Card>
          </>
          )}
        </>
      )}
    </div>
  );
}
